import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGrowthSquad } from "@/hooks/useGrowthSquad";
import { UserSelector } from "@/components/growth-squad/UserSelector";
import { useAuth } from "@/hooks/useAuth";
import { useBrandSelection } from "@/hooks/useBrandSelection";
import { useUserAccessibleBrands } from "@/hooks/useUserAccessibleBrands";
import { useSquadMemberDialog } from "@/contexts/SquadMemberDialogContext";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
} from "@/components/ui/dialog";

interface FormData {
  name: string;
  alias: string;
  email: string;
  user_id: string;
  type: 'Growth Partner' | 'Client' | 'Third Party';
  slack_id: string;
  origin_brand: string;
}

export const GlobalSquadMemberDialog = () => {
  const { isOpen, mode, editingMember, closeDialog, onSuccess } = useSquadMemberDialog();
  const { user } = useAuth();
  const { currentBrandId } = useBrandSelection();
  const { createSquadMember, updateSquadMember, isLoading } = useGrowthSquad();
  const { brands: accessibleBrands } = useUserAccessibleBrands();

  const [formData, setFormData] = useState<FormData>({
    name: "",
    alias: "",
    email: "",
    user_id: user?.id || "",
    type: "Growth Partner",
    slack_id: "",
    origin_brand: currentBrandId || ""
  });

  // Reset form when dialog opens/closes or editing member changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && editingMember) {
        setFormData({
          name: editingMember.name,
          alias: editingMember.alias,
          email: editingMember.email,
          user_id: editingMember.user_id,
          type: editingMember.type || "Growth Partner",
          slack_id: editingMember.slack_id || "",
          origin_brand: editingMember.origin_brand || editingMember.brand_id || currentBrandId || ""
        });
      } else {
        setFormData({
          name: "",
          alias: "",
          email: "",
          user_id: user?.id || "",
          type: "Growth Partner",
          slack_id: "",
          origin_brand: currentBrandId || ""
        });
      }
    }
  }, [isOpen, mode, editingMember, user?.id, currentBrandId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let success = false;
    
    if (mode === 'edit' && editingMember) {
      success = await updateSquadMember(editingMember.id, {
        name: formData.name,
        alias: formData.alias,
        email: formData.email,
        user_id: formData.user_id,
        type: formData.type,
        slack_id: formData.slack_id,
        origin_brand: formData.origin_brand || currentBrandId
      });
    } else {
      success = await createSquadMember({
        name: formData.name,
        alias: formData.alias,
        email: formData.email,
        user_id: formData.user_id || user?.id,
        brand_id: currentBrandId || "",
        type: formData.type,
        slack_id: formData.slack_id,
        origin_brand: formData.origin_brand || currentBrandId
      });
    }

    if (success) {
      onSuccess?.();
      closeDialog();
    }
  };

  const handleUserSelect = (userId: string) => {
    setFormData(prev => ({ ...prev, user_id: userId }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitleWithBug
            popupName={mode === 'edit' ? "Edit Squad Member" : "Add Squad Member"}
            popupContext="/growth-squad"
            isOpen={isOpen}
          >
            {mode === 'edit' ? "Edit Squad Member" : "Add Squad Member"}
          </DialogTitleWithBug>
          <DialogDescription className="text-sm">
            {mode === 'edit' ? "Update squad member information." : "Create a new squad member profile."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="space-y-2">
            <UserSelector
              currentUserId={formData.user_id}
              onUserSelect={handleUserSelect}
              label="User"
            />
            
            <div className="flex items-center gap-3">
              <Label htmlFor="name" className="w-16 text-xs">Name</Label>
              <Input
                id="name"
                className="flex-1 h-8 text-sm"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="alias" className="w-16 text-xs">Alias</Label>
              <Input
                id="alias"
                className="flex-1 h-8 text-sm"
                value={formData.alias}
                onChange={(e) => setFormData(prev => ({ ...prev, alias: e.target.value }))}
                required
              />
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="email" className="w-16 text-xs">Email</Label>
              <Input
                id="email"
                type="email"
                className="flex-1 h-8 text-sm"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="type" className="w-16 text-xs">Type</Label>
              <Select value={formData.type} onValueChange={(value) => setFormData(prev => ({ ...prev, type: value as 'Growth Partner' | 'Client' | 'Third Party' }))}>
                <SelectTrigger className="flex-1 h-8 text-sm">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Growth Partner">Growth Partner</SelectItem>
                  <SelectItem value="Client">Client</SelectItem>
                  <SelectItem value="Third Party">Third Party</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="slack-id" className="w-16 text-xs">Slack ID</Label>
              <Input
                id="slack-id"
                className="flex-1 h-8 text-sm"
                value={formData.slack_id}
                onChange={(e) => setFormData(prev => ({ ...prev, slack_id: e.target.value }))}
                placeholder="U1234567890"
              />
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="origin-brand" className="w-16 text-xs">Origin</Label>
              <Select 
                value={formData.origin_brand} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, origin_brand: value }))}
              >
                <SelectTrigger className="flex-1 h-8 text-sm">
                  <SelectValue placeholder="Select origin" />
                </SelectTrigger>
                <SelectContent>
                  {accessibleBrands.map(brand => (
                    <SelectItem key={brand.id} value={brand.id}>
                      {brand.name} {brand.accessType === 'owner' ? '(Owner)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="outline" size="sm" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={isLoading}>
                {mode === 'edit' ? "Update" : "Create"}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
